/* global Office Word */
import * as React from "react";
import { useState, useCallback } from "react";
import {
  Button,
  Card,
  CardHeader,
  Text,
  Spinner,
  makeStyles,
  tokens,
  Badge,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";
import {
  DocumentArrowUpRegular,
  ArrowSyncRegular,
  Checkmark24Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import { getDocumentFile, getDocumentFilename } from "../taskpane";
import { apiClient, UploadDocumentResponse } from "../../api";
import { AcceptRevisionsModal } from "./AcceptRevisionsModal"; // Import the modal

const useStyles = makeStyles({
  container: {
    padding: "16px",
  },
  card: {
    width: "100%",
  },
  cardContent: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
  },
  resultContainer: {
    marginTop: "12px",
  },
  statsContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "8px",
  },
  messageBar: {
    marginTop: "12px",
  },
});

interface UploadState {
  status: "idle" | "loading" | "success" | "error";
  result?: UploadDocumentResponse;
  error?: string;
  documentId?: string; // Keep track of uploaded document ID
  documentName?: string;
}

// Maximum file size allowed (10 MB)
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

interface DocumentUploadProps {
  onDocumentUploaded?: (documentId: string, documentName: string, agentId?: string) => void;
  onDocumentReset?: () => void;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({ 
  onDocumentUploaded, 
  onDocumentReset 
}) => {
  const styles = useStyles();
  const [uploadState, setUploadState] = useState<UploadState>({ status: "idle" });
  const [showAcceptRevisionsModal, setShowAcceptRevisionsModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const performUpload = useCallback(async () => {
    setUploadState((prev) => ({ ...prev, status: "loading", error: undefined }));

    try {
      // Get document file from Word (binary .docx with formatting)
      const fileBlob = await getDocumentFile();

      if (!fileBlob || fileBlob.size === 0) {
        throw new Error("Il documento è vuoto");
      }

      // Validate file size
      if (fileBlob.size > MAX_DOCUMENT_SIZE) {
        const sizeMB = (fileBlob.size / (1024 * 1024)).toFixed(1);
        const maxMB = (MAX_DOCUMENT_SIZE / (1024 * 1024)).toFixed(0);
        throw new Error(
          `Il documento è troppo grande. Massimo ${maxMB} MB consentiti, ` +
          `il documento è ${sizeMB} MB.`
        );
      }

      // Try to get filename
      const filename = await getDocumentFilename();
      const docName = filename || "documento.docx";

      let result: UploadDocumentResponse;

      // Check if we already have a document ID (update existing) or need to create new
      if (uploadState.documentId) {
        // Update existing document
        result = await apiClient.updateDocument(uploadState.documentId, {
          file: fileBlob,
          filename: docName,
        });
      } else {
        // Upload new document
        result = await apiClient.uploadDocument({
          file: fileBlob,
          filename: docName,
        });
      }

      setUploadState({
        status: "success",
        result,
        documentId: result.document_id,
        documentName: docName,
      });
      setLastUpdated(new Date()); // Set last updated timestamp
      onDocumentUploaded?.(result.document_id, docName, result.agent_id);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadState((prev) => ({
        ...prev,
        status: "error",
        error: error instanceof Error ? error.message : "Errore durante il caricamento",
      }));
    }
  }, [uploadState.documentId, onDocumentUploaded]);

  const handleUpload = useCallback(async () => {
    // Document.revisions is only available in WordApiDesktop 1.1 (not Word Online)
    const isDesktop = Office.context.requirements.isSetSupported("WordApiDesktop", "1.1");

    if (!isDesktop) {
      // Word Online: skip revision check and upload directly
      performUpload();
      return;
    }

    try {
      await Word.run(async (context) => {
        const revisions = context.document.revisions;
        context.load(revisions, "items");
        await context.sync();
        const hasPendingChanges = revisions.items.length > 0;

        if (hasPendingChanges) {
          setShowAcceptRevisionsModal(true);
        } else {
          // No tracked changes, proceed directly with upload
          performUpload();
        }
      });
    } catch (error) {
      console.warn("Revision check failed, proceeding with upload:", error);
      performUpload();
    }
  }, [performUpload]);

  const handleConfirmAcceptRevisions = useCallback(async () => {
    setShowAcceptRevisionsModal(false);
    try {
      await Word.run(async (context) => {
        context.document.acceptAllRevisions();
        await context.sync();
      });
    } catch (error) {
      console.warn("Error accepting revisions:", error);
    }
    performUpload();
  }, [performUpload]);

  const handleCancelAcceptRevisions = useCallback(() => {
    setShowAcceptRevisionsModal(false);
    // Do nothing, effectively canceling the upload
  }, []);

  const handleReset = useCallback(() => {
    setUploadState({ status: "idle", documentId: undefined, documentName: undefined });
    setLastUpdated(null); // Reset last updated timestamp
    onDocumentReset?.();
  }, [onDocumentReset]);

  // Enable Track Changes when component mounts (desktop only)
  React.useEffect(() => {
    if (!Office.context.requirements.isSetSupported("WordApiDesktop", "1.1")) {
      return;
    }
    Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();
    }).catch((error) => {
      console.error("Error enabling track changes:", error);
    });
  }, []); // Empty dependency array means this runs once on mount

  const isUpdate = !!uploadState.documentId;

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          image={<DocumentArrowUpRegular />}
          header={<Text weight="semibold">Carica Documento</Text>}
          description={
            <Text className={styles.description}>
              Carica il contenuto del documento per l'analisi
            </Text>
          }
        />
        <div className={styles.cardContent}>
          {uploadState.status === "idle" && (
            <>
              <Text>
                Clicca il pulsante per caricare il contenuto del documento corrente 
                nel sistema Legal Agent per l'analisi.
              </Text>
              <div className={styles.buttonContainer}>
                <Button
                  appearance="primary"
                  icon={<DocumentArrowUpRegular />}
                  onClick={handleUpload}
                >
                  Carica Documento
                </Button>
              </div>
            </>
          )}

          {uploadState.status === "loading" && (
            <div className={styles.buttonContainer}>
              <Spinner size="medium" label={isUpdate ? "Aggiornamento in corso..." : "Caricamento in corso..."} />
            </div>
          )}

          {uploadState.status === "success" && uploadState.result && (
            <div className={styles.resultContainer}>
              <MessageBar intent="success" className={styles.messageBar}>
                <MessageBarBody>
                  <MessageBarTitle>{isUpdate ? "Documento aggiornato!" : "Documento caricato!"}</MessageBarTitle>
                  {uploadState.result.message}
                </MessageBarBody>
              </MessageBar>
              <div className={styles.statsContainer}>
                <Badge appearance="outline" color="success" icon={<Checkmark24Regular />}>
                  ID: {uploadState.result.document_id}
                </Badge>
                <Badge appearance="outline" color="informative">
                  {uploadState.result.word_count} parole
                </Badge>
                {lastUpdated && (
                  <Badge appearance="outline" color="brand">
                    Aggiornato il: {lastUpdated.toLocaleString()}
                  </Badge>
                )}
              </div>
              <div className={styles.buttonContainer}>
                <Button 
                  appearance="primary" 
                  icon={<ArrowSyncRegular />}
                  onClick={handleUpload}
                >
                  Aggiorna Documento
                </Button>
                <Button appearance="secondary" onClick={handleReset}>
                  Nuovo Documento
                </Button>
              </div>
            </div>
          )}

          {uploadState.status === "error" && (
            <div className={styles.resultContainer}>
              <MessageBar intent="error" className={styles.messageBar}>
                <MessageBarBody>
                  <MessageBarTitle>Errore</MessageBarTitle>
                  {uploadState.error}
                </MessageBarBody>
              </MessageBar>
              <div className={styles.buttonContainer}>
                <Button
                  appearance="secondary"
                  icon={<Dismiss24Regular />}
                  onClick={handleReset}
                >
                  Riprova
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
      <AcceptRevisionsModal
        isOpen={showAcceptRevisionsModal}
        onConfirm={handleConfirmAcceptRevisions}
        onCancel={handleCancelAcceptRevisions}
      />
    </div>
  );
};

export default DocumentUpload;
